import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Carousel images showing team collaboration
  const carouselImages = [
    {
      src: "/assets/images/business_therapy_consultation_one.png",
      alt: "Professional team consultation session with diverse business experts collaborating around a conference table",
      title: "Collaboration d\'Experts",
      description: "Notre équipe en session de consultation stratégique"
    },
    {
      src: "/assets/images/business_therapy_transformation_two.png", 
      alt: "Business transformation team presenting innovative solutions and strategic planning on digital displays",
      title: "Transformation Collaborative",
      description: "Présentation d\'équipe pour la transformation digitale"
    },
    {
      src: "/assets/images/business_therapy_strategic_three.png",
      alt: "Strategic business therapy team conducing workshop with collaborative problem-solving methodology",
      title: "Innovation Stratégique",
      description: "Atelier d\'innovation en équipe multidisciplinaire"
    },
    {
      src: "/assets/images/hr_tech_dashboard_one.png",
      alt: "HR technology team presenting advanced analytics dashboard and human resources management solutions",
      title: "Excellence Technologique",
      description: "Présentation des solutions RH avancées"
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Carousel Background */}
      <div className="absolute inset-0 z-0">
        {carouselImages?.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="relative w-full h-full">
              <Image
                src={image?.src}
                alt={image?.alt}
                className="w-full h-full object-cover"
              />
              {/* Multiple darker overlay layers for significant darkening */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#D93B34]/40 via-[#9B8E56]/35 to-amber-900/40"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/60"></div>
              <div className="absolute inset-0 bg-black/30"></div>
            </div>
          </div>
        ))}
      </div>
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center space-x-3 bg-black/40 backdrop-blur-md text-white px-6 py-3 rounded-full font-semibold text-sm border border-white/20">
            <Icon name="Users" size={20} />
            <span>Notre Équipe d'Excellence CRF PERFECTION</span>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight">
            Notre{' '}
            <span className="relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-yellow-700 to-amber-800">
                Équipe
              </span>
              <div className="absolute -bottom-2 left-0 right-0 h-2 bg-gradient-to-r from-amber-700 to-yellow-800 rounded-full"></div>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl lg:text-3xl text-white/90 max-w-4xl mx-auto leading-relaxed font-light">
            L'expertise et la passion humaine derrière l'excellence de CRF Perfection
          </p>

          {/* Current slide info */}
          <div className="bg-black/30 backdrop-blur-md rounded-2xl p-6 max-w-2xl mx-auto border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-2">
              {carouselImages?.[currentSlide]?.title}
            </h3>
            <p className="text-white/80 text-lg">
              {carouselImages?.[currentSlide]?.description}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto pt-8">
            <div className="bg-gradient-to-br from-[#D93B34]/15 via-black/40 to-red-900/25 backdrop-blur-md rounded-2xl p-6 border border-[#D93B34]/20">
              <div className="w-12 h-12 bg-[#D93B34]/80 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Award" size={24} className="text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white">18+</h3>
              <p className="text-white/80 text-sm">Années d'Expérience</p>
            </div>
            
            <div className="bg-gradient-to-br from-[#9B8E56]/15 via-black/40 to-[#9B8E56]/25 backdrop-blur-md rounded-2xl p-6 border border-[#9B8E56]/20">
              <div className="w-12 h-12 bg-[#9B8E56]/80 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={24} className="text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white">25+</h3>
              <p className="text-white/80 text-sm">Experts Dédiés</p>
            </div>

            <div className="bg-gradient-to-br from-amber-900/15 via-black/40 to-yellow-900/25 backdrop-blur-md rounded-2xl p-6 border border-amber-700/20">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-700 to-yellow-800 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="GraduationCap" size={24} className="text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white">50+</h3>
              <p className="text-white/80 text-sm">Certifications</p>
            </div>

            <div className="bg-gradient-to-br from-orange-900/15 via-black/40 to-red-900/25 backdrop-blur-md rounded-2xl p-6 border border-orange-700/20">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-700 to-red-800 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Trophy" size={24} className="text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white">100+</h3>
              <p className="text-white/80 text-sm">Projets Réussis</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 pt-8">
            <button className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-700 to-yellow-800 text-white hover:from-amber-800 hover:to-yellow-900 px-8 py-4 rounded-xl font-bold shadow-2xl hover:shadow-amber-900/25 transform hover:-translate-y-1 transition-all duration-300">
              <Icon name="Users" size={20} />
              <span>Découvrir l'Équipe</span>
            </button>
            
            <button className="inline-flex items-center space-x-3 border-2 border-white/30 text-white hover:bg-white/10 hover:text-white px-8 py-4 rounded-xl font-semibold backdrop-blur-md transition-all duration-300">
              <Icon name="MessageSquare" size={20} />
              <span>Nous Contacter</span>
            </button>
          </div>
        </div>
      </div>
      {/* Carousel Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {carouselImages?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-amber-600 w-8' :'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </div>
      {/* Carousel Navigation */}
      <button
        onClick={() => setCurrentSlide((prev) => (prev - 1 + carouselImages?.length) % carouselImages?.length)}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/40 hover:bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center transition-all duration-300 border border-white/20"
      >
        <Icon name="ChevronLeft" size={20} className="text-white" />
      </button>
      <button
        onClick={() => setCurrentSlide((prev) => (prev + 1) % carouselImages?.length)}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/40 hover:bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center transition-all duration-300 border border-white/20"
      >
        <Icon name="ChevronRight" size={20} className="text-white" />
      </button>
      {/* Floating Elements - significantly darkened */}
      <div className="absolute top-20 left-20 w-20 h-20 bg-gradient-to-br from-amber-900/15 to-yellow-900/15 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-40 right-20 w-32 h-32 bg-gradient-to-br from-[#D93B34]/10 to-red-900/15 rounded-full blur-2xl animate-pulse"></div>
      <div className="absolute top-1/3 right-10 w-16 h-16 bg-gradient-to-br from-[#9B8E56]/15 to-amber-900/15 rounded-full blur-lg animate-pulse"></div>
    </section>
  );
};

export default HeroSection;